import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Paginate, { PaginateResource } from '../../../interfaces/paginate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons';
import css from './index.module.css';

interface Props {
  response: PaginateResource<any> | Paginate<any>;
  hideRecords?: boolean;
  paginate?: boolean;
}

export default function Paging({
  response,
  hideRecords = false,
  paginate = true,
}: Props) {
  const { t } = useTranslation();
  const meta = 'meta' in response ? response.meta : response;

  function records() {
    return hideRecords ? null : (
      <div className={css['records']}>
        <span>
          {meta.from} - {meta.to} {t('from')} {meta.total}
        </span>
      </div>
    );
  }

  function buttons() {
    const links = meta.links.filter((link) => {
      // refuse nonexistent Previous or Next page
      if (!link.url) {
        return false;
      }
      // take 2 pages before and after the current page
      if (typeof link.label === 'number') {
        return (
          link.label >= meta.current_page - 2 &&
          link.label <= meta.current_page + 2
        );
      }
      // take existing Previous or Next page
      return true;
    });

    function search(search: string, pageUrl: string) {
      const pageParams = new URLSearchParams(new URL(pageUrl).search);
      const params = new URLSearchParams(search);
      params.set('page', pageParams.get('page'));
      return '?'.concat(params.toString());
    }

    function content(label: string) {
      if (label === '&laquo; Previous') {
        return <FontAwesomeIcon icon={faChevronLeft} />;
      } else if (label === 'Next &raquo;') {
        return <FontAwesomeIcon icon={faChevronRight} />;
      } else {
        return label;
      }
    }

    return (
      <div className={css['buttons']}>
        {links.map((link, index) => (
          <div className={link.active ? css['active'] : ''} key={link.label}>
            <Link
              to={(location) => ({
                ...location,
                search: search(location.search, link.url),
              })}
            >
              {content(link.label)}
            </Link>
          </div>
        ))}
      </div>
    );
  }

  if (!paginate) {
    return null;
  }

  if (response && response.data.length > 0) {
    return (
      <div
        className={`${css['paging']} ${hideRecords ? css['no-records'] : ''}`}
      >
        {records()}
        {buttons()}
      </div>
    );
  } else {
    return null;
  }
}
